import { useEffect, useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Breadcrumbs, Tabs, TabsContent, TabsList, TabsTrigger } from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { useAdminChallenge } from '@/hooks/useAdminChallenge'
import { useActivityReport } from '@/hooks/useActivityReport'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { StandardLoader } from '@/components/StandardLoader'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { ActivitySummary } from './ActivitySummary'
import { ActivityLedger, ruleAnchorId } from './ActivityLedger'
import { ActivityGrowthChart } from './ActivityGrowthChart'
import { splitSessions, type Ev } from './sessions'

type Tab = 'ledger' | 'growth'

export const ActivityReportPage = ({ slug }: { slug: string }) => {
  const { t } = useTar()
  const { challengeInfo, isLoading: challengeLoading } = useAdminChallenge(slug)
  const challengeId = challengeInfo?.challenge.id
  const { data: report, isPending, isError } = useActivityReport(challengeId)
  const [tab, setTab] = useState<Tab>('ledger')
  const [pendingRule, setPendingRule] = useState<string | null>(null)
  // Sessionised once here; the summary, ledger and chart all read the same split.
  const split = useMemo(() => (report === undefined ? undefined : splitSessions(report.events as Ev[])), [report])
  // Runs after the ledger tab has mounted, so the anchor exists.
  useEffect(() => {
    if (pendingRule === null || tab !== 'ledger') return
    document.getElementById(ruleAnchorId(pendingRule))?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    setPendingRule(null)
  }, [pendingRule, tab])
  useDocumentTitle(t('adminActivityReport.title'), 'admin')

  if (challengeLoading || (challengeId !== undefined && isPending)) {
    return <div className="flex items-center justify-center py-16"><StandardLoader /></div>
  }
  if (challengeId === undefined || isError || report === undefined || split === undefined) {
    return <NoDataEmptyState title={t('adminActivityReport.loadError')} />
  }

  const jumpToRule = (rule: string) => { setTab('ledger'); setPendingRule(rule) }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('challenges.breadcrumbAdmin'), href: '/admin' },
          { label: t('challenges.breadcrumbChallenges'), href: '/admin/challenges' },
          { label: challengeInfo?.task?.name ?? slug, href: `/admin/challenges/${slug}` },
          { label: t('adminActivityReport.breadcrumb') },
        ]}
        renderLink={(item, children) => <Link to={item.href}>{children}</Link>}
      />
      <div className="grid gap-10 md:grid-cols-[280px_minmax(0,1fr)]">
        <div className="md:sticky md:top-4 md:self-start">
          <ActivitySummary report={report} sessionCount={split.sessions.length} onJumpToRule={jumpToRule} />
        </div>
        {report.events.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('adminActivityReport.empty')}</p>
        ) : (
          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList>
              <TabsTrigger value="ledger">{t('adminActivityReport.tabs.ledger')}</TabsTrigger>
              <TabsTrigger value="growth">{t('adminActivityReport.tabs.growth')}</TabsTrigger>
            </TabsList>
            <TabsContent value="ledger" className="pt-4"><ActivityLedger split={split} /></TabsContent>
            <TabsContent value="growth" className="pt-4"><ActivityGrowthChart split={split} /></TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  )
}
